import { apiGet, apiPut } from '../lib/apiClient';

const SETTINGS_KEY = 'admin';

const defaultAttendanceSettings = {
  latitude: -8.6500,
  longitude: 115.2167,
  radius: 100,
  latitude2: null,
  longitude2: null,
  radius2: null,
  attendancePhotoRequired: false,
  lateThresholdTime: '09:00',
  checkoutTime: '16:00',
  checkoutSchedule: {
    monday: '16:00',
    tuesday: '16:00',
    wednesday: '16:00',
    thursday: '16:00',
    friday: '15:00',
    saturday: '15:00',
    sunday: '16:00',
  },
};

const defaultEmployeeIdSettings = {
  prefix: 'DWT',
  internPrefix: 'INT',
  separator: '-',
  padding: 4,
  nextNumber: 1,
};

const INTERN_RE = /\b(?:magang|pkl|intern(?:ship)?)\b|praktik\s*kerja|praktek\s*kerja/;

export const isInternEmployeeMeta = (meta = {}) => {
  const blob = [meta.role, meta.position, meta.department, meta.departmentName]
    .map((v) => String(v || '').toLowerCase())
    .join(' ');
  return INTERN_RE.test(blob);
};

const normalizeEmployeeIdSettings = (settings = {}) => {
  const raw = settings.employeeId || settings;
  const prefix = String(raw.prefix || defaultEmployeeIdSettings.prefix).trim().toUpperCase().replace(/[^A-Z0-9]/g, '') || defaultEmployeeIdSettings.prefix;
  const internPrefix = String(raw.internPrefix || defaultEmployeeIdSettings.internPrefix).trim().toUpperCase().replace(/[^A-Z0-9]/g, '') || defaultEmployeeIdSettings.internPrefix;
  const separator = String(raw.separator ?? defaultEmployeeIdSettings.separator).slice(0, 3);
  const padding = Math.min(10, Math.max(1, Number(raw.padding) || defaultEmployeeIdSettings.padding));
  const nextNumber = Math.max(1, Number(raw.nextNumber) || defaultEmployeeIdSettings.nextNumber);

  return { prefix, internPrefix, separator, padding, nextNumber };
};

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const isValidTime = (value) => typeof value === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

const normalizeCheckoutSchedule = (settings = {}) => {
  const defaultTime = isValidTime(settings.checkoutTime) ? settings.checkoutTime : defaultAttendanceSettings.checkoutTime;
  const source = settings.checkoutSchedule || {};

  return Object.fromEntries(
    Object.entries(defaultAttendanceSettings.checkoutSchedule).map(([day, fallback]) => [
      day,
      isValidTime(source[day]) ? source[day] : defaultTime || fallback,
    ])
  );
};

const normalizeAttendanceSettings = (settings = {}) => {
  const latitude = toFiniteNumber(settings.latitude ?? settings.lat);
  const longitude = toFiniteNumber(settings.longitude ?? settings.lng);
  const radius = toFiniteNumber(settings.radius);
  const latitude2 = toFiniteNumber(settings.latitude2 ?? settings.lat2);
  const longitude2 = toFiniteNumber(settings.longitude2 ?? settings.lng2);
  const radius2 = toFiniteNumber(settings.radius2);

  const checkoutTime = isValidTime(settings.checkoutTime) ? settings.checkoutTime : defaultAttendanceSettings.checkoutTime;
  const checkoutSchedule = normalizeCheckoutSchedule({ ...settings, checkoutTime });

  return {
    ...defaultAttendanceSettings,
    ...settings,
    latitude: latitude ?? defaultAttendanceSettings.latitude,
    longitude: longitude ?? defaultAttendanceSettings.longitude,
    radius: radius ?? defaultAttendanceSettings.radius,
    latitude2,
    longitude2,
    radius2,
    attendancePhotoRequired: settings.attendancePhotoRequired === true,
    lateThresholdTime: isValidTime(settings.lateThresholdTime) ? settings.lateThresholdTime : defaultAttendanceSettings.lateThresholdTime,
    checkoutTime,
    checkoutSchedule,
  };
};

async function loadAdminSettings() {
  try {
    return await apiGet(`/settings/${SETTINGS_KEY}`);
  } catch (error) {
    if (error.status === 404) return {};
    throw error;
  }
}

async function saveAdminSettings(patch) {
  const current = await loadAdminSettings();
  await apiPut(`/settings/${SETTINGS_KEY}`, { ...current, ...patch });
}

export const getAttendanceSettings = async () => {
  try {
    const data = normalizeAttendanceSettings(await loadAdminSettings());

    if (typeof data.latitude !== 'number' || typeof data.longitude !== 'number' || typeof data.radius !== 'number') {
      throw new Error('Data pengaturan absensi utama tidak lengkap atau formatnya salah.');
    }

    const hasLat2 = typeof data.latitude2 === 'number';
    const hasLng2 = typeof data.longitude2 === 'number';
    const hasRad2 = typeof data.radius2 === 'number';

    if (hasLat2 || hasLng2 || hasRad2) {
      if (!hasLat2 || !hasLng2 || !hasRad2) {
        throw new Error('Data pengaturan absensi kedua tidak lengkap atau formatnya salah.');
      }
    }
    return data;
  } catch (error) {
    console.error('Error getting attendance settings:', error);
    throw error;
  }
};

export const getEmployeeIdSettings = async () => {
  try {
    const data = await loadAdminSettings();
    return normalizeEmployeeIdSettings(data);
  } catch (error) {
    console.error('Error getting employee ID settings:', error);
    throw error;
  }
};

export const updateEmployeeIdSettings = async (settings) => {
  try {
    const normalized = normalizeEmployeeIdSettings(settings);
    await saveAdminSettings({ employeeId: normalized });
    return normalized;
  } catch (error) {
    console.error('Error updating employee ID settings:', error);
    throw new Error('Gagal menyimpan pengaturan ID karyawan.');
  }
};

export const previewEmployeeId = (settings, meta = {}) => {
  const normalized = normalizeEmployeeIdSettings(settings);
  const prefix = isInternEmployeeMeta(meta) ? normalized.internPrefix : normalized.prefix;
  return `${prefix}${normalized.separator}${String(normalized.nextNumber).padStart(normalized.padding, '0')}`;
};

/** Generate ID via server (sumber kebenaran: settings DWT / INT). */
export const generateNextEmployeeId = async (meta = {}) => {
  const qs = new URLSearchParams();
  if (meta.role) qs.set('role', meta.role);
  if (meta.position) qs.set('position', meta.position);
  if (meta.department) qs.set('department', meta.department);
  const suffix = qs.toString() ? `?${qs}` : '';
  const result = await apiGet(`/employees/next-id${suffix}`);
  if (!result?.employeeId) throw new Error('Gagal generate ID karyawan');
  return result.employeeId;
};

export const updateAttendanceSettings = async (settings) => {
  try {
    const normalized = normalizeAttendanceSettings(settings);

    if (typeof normalized.latitude !== 'number' || typeof normalized.longitude !== 'number' || typeof normalized.radius !== 'number') {
      throw new Error('Data pengaturan lokasi utama tidak valid.');
    }

    const hasLatitude2 = normalized.latitude2 !== null && normalized.latitude2 !== undefined && normalized.latitude2 !== '';
    const hasLongitude2 = normalized.longitude2 !== null && normalized.longitude2 !== undefined && normalized.longitude2 !== '';
    const hasRadius2 = normalized.radius2 !== null && normalized.radius2 !== undefined && normalized.radius2 !== '';

    if (hasLatitude2 || hasLongitude2 || hasRadius2) {
      if (typeof normalized.latitude2 !== 'number' || typeof normalized.longitude2 !== 'number' || typeof normalized.radius2 !== 'number') {
        throw new Error('Data pengaturan lokasi kedua harus diisi lengkap (latitude, longitude, radius) dan harus berupa angka.');
      }
    }

    if (!isValidTime(normalized.lateThresholdTime)) {
      throw new Error('Jam batas terlambat tidak valid.');
    }

    if (!isValidTime(normalized.checkoutTime) || Object.values(normalized.checkoutSchedule).some((time) => !isValidTime(time))) {
      throw new Error('Jam pulang harus memakai format HH:mm yang valid.');
    }

    const current = await loadAdminSettings();
    await saveAdminSettings({
      ...current,
      ...normalized,
      latitude2: hasLatitude2 ? normalized.latitude2 : null,
      longitude2: hasLongitude2 ? normalized.longitude2 : null,
      radius2: hasRadius2 ? normalized.radius2 : null,
    });
  } catch (error) {
    console.error('Error updating attendance settings:', error);
    throw new Error('Gagal menyimpan pengaturan absensi.');
  }
};
